import { useEffect, useMemo, useState } from "react";
import "./onehealth.css";
import { LangProvider, useLang } from "./OneHealthContext";
import { LTBrand, PoweredByCarePlix, VerdictBadge } from "./components";
import FaceScan from "./steps/FaceScan";
import { commonEmbedParams, deliverToHost } from "./embedBridge";
import { LANGS } from "./i18n";

/* ============================================================
   EMBED / WEBVIEW MODE — standalone face-scan journey.

   URL:  /embed/facescan?employee_id=EMP123&name=John%20Doe&age=34
           &gender=Male&height=175&weight=72[&lang=en][&redirect_url=...]

   Required params: name, age, gender (Male|Female|Other), height (cm), weight (kg)
   Optional: employee_id, lang (en|hi|ar|fr), redirect_url (http(s)/app scheme only),
             host_origin (locks postMessage delivery to that exact origin)

   Result delivery (all channels attempted, in order):
   1. window.parent / window.opener postMessage  → { type: "careplix.facescan.result", ... }
   2. React Native WebView:   window.ReactNativeWebView.postMessage(json)
   3. Android JS interface:   window.OneHealthBridge.onFaceScanResult(json)
   4. Flutter JS channel:     window.OneHealthChannel.postMessage(json)
   5. iOS WKWebView handler:  webkit.messageHandlers.oneHealthFaceScan.postMessage(json)
   6. redirect_url (if given): redirected to with ?result=<base64url(JSON)>

   The payload is the SANITIZED server report only (verdict, wellbeing, RAG
   params) — raw vitals/thresholds never reach the browser (server add-scan).
   ============================================================ */

function parseParams() {
  const q = new URLSearchParams(window.location.search);
  const get = (k) => (q.get(k) || "").trim();
  const genderRaw = get("gender").toLowerCase();
  const gender = genderRaw === "male" ? "Male" : genderRaw === "female" ? "Female" : genderRaw === "other" ? "Other" : "";

  const p = {
    ...commonEmbedParams(q),
    name: get("name").slice(0, 120),
    age: Number(get("age")),
    gender,
    height: Number(get("height")),
    weight: Number(get("weight")),
  };
  const inRange = (v, lo, hi) => Number.isFinite(v) && v >= lo && v <= hi;
  const missing = [];
  if (!p.name) missing.push("name");
  if (!inRange(p.age, 1, 120)) missing.push("age (1–120)");
  if (!p.gender) missing.push("gender (Male|Female|Other)");
  if (!inRange(p.height, 50, 250)) missing.push("height (50–250 cm)");
  if (!inRange(p.weight, 20, 300)) missing.push("weight (20–300 kg)");
  return { p, missing };
}

function EmbedFlow() {
  const { dir, setLang } = useLang();
  const { p, missing } = useMemo(parseParams, []);
  const [done, setDone] = useState(null); // sanitized result after delivery

  // Language forced by the host via ?lang=
  useEffect(() => {
    if (p.lang && LANGS.some((l) => l.code === p.lang)) setLang(p.lang);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Bad integration call — tell the host app too, not just the screen.
  useEffect(() => {
    if (missing.length) {
      deliverToHost({ type: "careplix.facescan.error", error: "missing_params", missing }, "", p.hostOrigin);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onScanDone = (payload, o) => {
    const message = {
      type: "careplix.facescan.result",
      ok: true,
      employee_id: p.employeeId,
      scan_token: o?.scanToken || null,
      verdict: payload.verdict,
      wellbeing: payload.wellbeing,
      wellbeingStatus: payload.wellbeingStatus,
      params: payload.params,
      employee: payload.employee,
      completed_at: new Date().toISOString(),
    };
    deliverToHost(message, p.redirectUrl, p.hostOrigin);
    setDone(message);
  };

  let screen;
  if (missing.length) {
    screen = (
      <div className="oh-view" style={{ justifyContent: "center", textAlign: "center", padding: 28 }}>
        <h2 style={{ marginBottom: 10 }}>Missing parameters</h2>
        <p className="oh-foot">
          This URL must include: <b>{missing.join(", ")}</b>
        </p>
        <p className="oh-foot" style={{ marginTop: 14, direction: "ltr" }}>
          /embed/facescan?employee_id=EMP123&amp;name=John&amp;age=34&amp;gender=Male&amp;height=175&amp;weight=72
        </p>
      </div>
    );
  } else if (done) {
    screen = (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center", padding: 28 }}>
        <VerdictBadge status={done.wellbeingStatus || "green"} />
        <h2 style={{ marginTop: 16 }}>Scan complete</h2>
        <p className="oh-foot" style={{ marginTop: 10 }}>
          The scan result has been sent back to the app. You can close this screen.
        </p>
      </div>
    );
  } else {
    screen = <FaceScan onDone={onScanDone} prefill={p} />;
  }

  return (
    <div className="oh-stage" dir={dir}>
      <div className="oh-device">
        <div className="oh-brandtop">
          <LTBrand />
          <PoweredByCarePlix />
        </div>
        <div className="oh-screen">{screen}</div>
      </div>
    </div>
  );
}

export default function EmbedFaceScan() {
  return (
    <LangProvider>
      <EmbedFlow />
    </LangProvider>
  );
}
